import _ from 'lodash';
import { FOX } from 'helpers/ContentHelper';
import {
  Events,
  getTealiumPlayerEvents,
  handleAnalytics,
  TrackEvents as GeneralTrackEvents,
} from '@foxcorp/delta-web-analytics';
import { getPlayerPlatform } from './BreakpointHelper';

export const PLAYER_REFERENCE_DEFAULT = 'default';
export const PLAYER_REFERENCE_GLOBAL = 'global';
export const PLAYER_REFERENCE_BANNER = 'banner';
export const CONTINUOUS = 'continuous';
export const LIVE = 'live';
export const PLAYER_CONTEXT_LIVE = 'PLAYER_CONTEXT_LIVE';
export const PLAYER_CONTEXT_WATCH = 'PLAYER_CONTEXT_WATCH';
export const PLAYER_TYPE_DEFAULT = 'standard';
export const PLAYER_TYPE_HERO = 'hero';
export const PLAYLIST = 'playlist';
const PREVIEW_PASS_5_MVPD = 'TempPass_fbcfox_5min';
const PREVIEW_PASS_60_MVPD = 'TempPass_fbcfox_60min';
export const RESTART = 'restart';
const STILL_WATCHING_HIDE_SECONDS = _.get(
  window.ExternalConfig,
  'stillWatching.messageDurationSeconds',
  5 * 60,
);
const STILL_WATCHING_HIDE_DURATION = STILL_WATCHING_HIDE_SECONDS
  ? STILL_WATCHING_HIDE_SECONDS * 1000
  : null;
const STILL_WATCHING_SHOW_DURATION = 180 * 60 * 1000; // 3 hours
export const VOD = 'vod';
export const PLAYER_STATES = {
  AD: 'AD',
  BUFFERING: 'BUFFERING',
  COMPLETE: 'COMPLETE',
  IDLE: 'IDLE',
  PAUSED: 'PAUSED',
  PLAYING: 'PLAYING',
  READY: 'READY',
  SEEKING: 'SEEKING',
  STOPPED: 'STOPPED',
};
export const INTERRUPTION_METHODS = {
  BROWSER_ACTION: 'browser action',
};
export const isPlayerTypeHero = type => type === PLAYER_TYPE_HERO;
export const getPlayer = (playerReference = PLAYER_REFERENCE_DEFAULT) =>
  _.get(window.player, playerReference, null);
/**
 * Return the JS path for the player
 * @return {String}             - JS path for the player
 */
export const getPlayerJs = () => window.ExternalConfig.playerUrl;

/**
 * Return the csid (site section ID) for the player
 * @return {String}             - Site section ID for the player
 */
export const getPlayerCsid = ({ showCode, isFullEpisode }) => {
  const platform = getPlayerPlatform();
  const videoType = isFullEpisode ? 'full-episodes' : 'clips';

  if (!showCode) {
    return null;
  }

  return `${platform}.fox.com/${showCode}/${videoType}`;
};

export const playerSetUserActive = (playerReference, active = true) => {
  const player = getPlayer(playerReference);
  if (player && player.setUserActive) player.setUserActive(active);
};

export const playerControlsVisible = playerReference =>
  _.get(getPlayer(playerReference), 'controlsShowing', true);

export const getChannelBackgroundUrl = channel => {
  if (!channel) return null;
  const backgroundUrl = window.ExternalConfig.networkFallbackArt.replace('{network}', FOX);
  return backgroundUrl;
};

export const playerPause = playerReference => {
  const player = getPlayer(playerReference);
  if (player && player.pause) player.pause();
};

export const playerRemove = (playerReference = PLAYER_REFERENCE_DEFAULT) => {
  const player = getPlayer(playerReference);
  if (player && player.remove) {
    player.remove();
    delete window.player[playerReference];
  }
};

export const getPlayerState = playerReference =>
  _.get(getPlayer(playerReference), 'state', PLAYER_STATES.STOPPED);

const playerGetPaused = playerReference => {
  const player = getPlayer(playerReference);
  return player && player.paused;
};
export const getActivePlayer = () => {
  if (_.isEmpty(window.player)) return null;
  let activePlayer = null;
  _.forEach(window.player, (player, reference) => {
    if (!playerGetPaused(reference)) activePlayer = reference;
  });
  return activePlayer;
};
export const playerPlay = playerReference => {
  const players = _.get(window, 'player', {});
  _.forEach(players, (player, reference) => {
    if (!player) return;
    if (reference === playerReference) {
      player.play();
    } else {
      player.pause();
    }
  });
};
export const playerHideControls = playerReference => {
  const player = getPlayer(playerReference);
  if (player) player.hideControls();
};
export const updatePlayerType = (type, playerReference) => {
  const player = getPlayer(playerReference);
  if (player && player.updatePlayerType && type) {
    player.updatePlayerType(type);
  }
};
export const playerSetFullscreen = (val, playerReference) => {
  const player = getPlayer(playerReference);
  if (player) player.player.setFullscreen(val);
};
export function setStillWatchingShowTimeout({ duration, cb }) {
  const { setTimeout } = this.props;
  return setTimeout(
    cb,
    duration ||
      _.get(window, 'context.player.stillWatching.showDuration') ||
      STILL_WATCHING_SHOW_DURATION,
  );
}
export function setStillWatchingHideTimeout({ duration, cb }) {
  const { setTimeout } = this.props;
  return setTimeout(
    cb,
    duration ||
      _.get(window, 'context.player.stillWatching.hideDuration') ||
      STILL_WATCHING_HIDE_DURATION,
  );
}
export function stillWatchingHideCb() {
  this.setState({ showStillWatching: false, isVideoOverlayPresent: false });
}
export function stillWatchingShowCb({ profileId }) {
  const {
    [GeneralTrackEvents.PLAYER_IDLE_PROMPT]: onPlayerIdleAnalytics,
  } = getTealiumPlayerEvents({ profileId });
  onPlayerIdleAnalytics();
  handleAnalytics({ event: Events.IDLE_PROMPT });
  this.setState({ showStillWatching: true, isVideoOverlayPresent: true });
}

export const getDuration = playerReference => _.get(getPlayer(playerReference), 'duration');
const getSecondsViewed = playerReference =>
  _.round(_.get(getPlayer(playerReference), 'secondsViewed'));

export const getPercentComplete = ({ playerReference, durationInSeconds }) => {
  const playerDuration = getDuration(playerReference);
  const duration = !playerDuration || playerDuration === 0 ? durationInSeconds : playerDuration;
  const secondsViewed = getSecondsViewed(playerReference);
  if (duration) return _.round((secondsViewed / duration) * 100);
  return null;
};
// Gets content position (excluding stitched Ads)
export const getContentPosition = playerReference =>
  _.get(getPlayer(playerReference), 'currentTime', 0);

export const isPreviewPass = mvpdId =>
  mvpdId === PREVIEW_PASS_5_MVPD || mvpdId === PREVIEW_PASS_60_MVPD;
